import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();
    this.refs.quantityInput.addEventListener('keydown', this.onQuantityInputKeyDown);
  }

  disconnectedCallback() {
    this.refs.quantityInput.removeEventListener('keydown', this.onQuantityInputKeyDown);
    super.disconnectedCallback();
  }

  /**
   * Prevent Enter from submitting the outer cart form and commit quantity change.
   * @param {KeyboardEvent} event
   */
  onQuantityInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.setQuantity(event);
  };

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states.
   * On the cart page, we synchronize the input's actual value and lastCommittedQuantity
   * directly with the confirmed quantity from the server.
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { cartQuantity } = this.getCurrentValues();

    quantityInput.value = cartQuantity.toString();
    this.lastCommittedQuantity = cartQuantity;
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
